/**
 * Panel 3 — `Tech Table Appointments` (§A5, Matrix 4).
 *
 * The per-official summary, and the home of the numbers the separate Workload panel used to
 * carry. Folding it in was the design's own instruction: the mockup puts the per-official
 * counts here and nowhere else, and two panels printing the same officials' totals — one live
 * and one from the server's last response — were a pair that could disagree on screen.
 *
 * What survives from the Workload panel is the thing it was for: `|SJ − TJ|`, the quantity
 * §A4 rule 4 measures, kept as its own column so an imbalance is a figure the TD can point
 * at rather than an inference from two other columns. The **verdict** on that spread is still
 * the server's — `balanced` is the tournament's configured tolerance applied by the engine,
 * and this panel prints it rather than recomputing it.
 *
 * The `completed / appointed` pair the design draft shows is deliberately absent; see the
 * note beside the head.
 */

import { useEffect, useState } from "react";

import type { CompetitionFilter, RoleFilter, TechRow } from "./derive.js";
import { COMPETITION_TABS, ROLE_TABS, Tabs } from "./Tabs.js";
import { TipsFold } from "./TipsFold.js";
import { cellClass } from "./useCellFlash.js";

/**
 * §A5 Matrix 4 drew this at page size 8; raised to 16 on the TD's ask (20 Sep, matchday 2)
 * so the whole Judges panel — 13 of them at this tournament — reads on ONE page. The pager
 * stays for the day a role tab holds more than sixteen.
 */
const PAGE = 16;

export interface TechTablePanelProps {
  rows: readonly TechRow[];
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  role: RoleFilter;
  onRole: (value: RoleFilter) => void;
  /** The server's own verdict on §A4 rule 4 — never recomputed on this side. */
  balanced: boolean;
  flashing: ReadonlySet<string>;
}

export function TechTablePanel({
  rows,
  competition,
  onCompetition,
  role,
  onRole,
  balanced,
  flashing,
}: TechTablePanelProps): React.JSX.Element {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));

  // A filter that shortens the list must not strand the reader on a page that no longer
  // exists — the pager would then say "Page 3 Of 1" over an empty table.
  useEffect(() => {
    setPage((current) => Math.min(current, pages - 1));
  }, [pages]);

  const shown = rows.slice(page * PAGE, page * PAGE + PAGE);

  return (
    <section className="panel panel--d3" aria-labelledby="techTitle" data-howto="tech-table-panel">
      <div className="panel__head">
        <h3 className="panel__title" id="techTitle">
          Tech Table Appointments
        </h3>
        <span className="spacer" />
        <span
          className={balanced ? "statuspill statuspill--good" : "statuspill statuspill--warn"}
          data-testid="balance-pill"
        >
          <span className="sdot" aria-hidden="true" />
          {balanced ? "SJ / TJ Balanced" : "SJ / TJ Spread Outside Tolerance"}
        </span>
        <span className="filtlab">Competition</span>
        <Tabs
          label="Tech table competition filter"
          options={COMPETITION_TABS}
          value={competition}
          onChange={(value) => {
            setPage(0);
            onCompetition(value);
          }}
          tint="competition"
        />
        <span className="filtlab">Role</span>
        <Tabs
          label="Tech table role filter"
          options={ROLE_TABS}
          value={role}
          onChange={(value) => {
            setPage(0);
            onRole(value);
          }}
        />
      </div>

      <table className="mtable sumtable" aria-label="Technical officials appointment counts">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Country</th>
            <th scope="col">Role</th>
            <th scope="col">TO</th>
            <th scope="col">SJ</th>
            <th scope="col">TJ</th>
            <th scope="col">Total</th>
            <th scope="col">|SJ − TJ|</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const id = row.official.tmsPersonId;
            const zero = row.total === 0;
            return (
              <tr
                key={id}
                className={row.official.gender === null ? undefined : `g-${row.official.gender}`}
                data-testid={`workload-${id}`}
              >
                <td className="cell-name">{row.official.mediaName}</td>
                <td className="cell-ctry">{row.official.country}</td>
                <td className="cell-role">{row.official.role}</td>
                <td className={cellClass(`cell-cnt${zero ? " z" : ""}`, `t:${id}:TO`, flashing)}>
                  {row.to}
                </td>
                <td className={cellClass(`cell-cnt${zero ? " z" : ""}`, `t:${id}:SJ`, flashing)}>
                  {row.sj}
                </td>
                <td className={cellClass(`cell-cnt${zero ? " z" : ""}`, `t:${id}:TJ`, flashing)}>
                  {row.tj}
                </td>
                <td className={cellClass("cell-tot", `t:${id}:tot`, flashing)}>{row.total}</td>
                <td className={cellClass("cell-cnt", `t:${id}:dif`, flashing)}>{row.difference}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {rows.length === 0 ? (
        <div className="emptystate">
          <b>Nobody is appointed yet.</b>
          The counts appear as soon as the schedule carries an appointment.
        </div>
      ) : null}

      <div className="pager" aria-label="Tech table pages">
        <span className="pager__info num" data-testid="tech-pager">
          Page {page + 1} Of {pages} · {rows.length} Officials
        </span>
        <button
          type="button"
          disabled={page === 0}
          onClick={() => {
            setPage((current) => Math.max(0, current - 1));
          }}
        >
          ‹ Prev
        </button>
        <button
          type="button"
          disabled={page >= pages - 1}
          onClick={() => {
            setPage((current) => Math.min(pages - 1, current + 1));
          }}
        >
          Next ›
        </button>
      </div>

      {/* Folded for the same reason the Fixture List's foot is: this prose was costing the
          TD table rows, and it is standing guidance, not a live warning. */}
      <div className="panel__foot">
        <TipsFold storageKey="hockeytd.tips.tech-table" testId="tech-tips">
          <b>Counted from the rows above —</b> every figure here is derived from the fixture
          list on this screen, so it moves the moment you change an appointment. The{" "}
          <b>SJ / TJ</b> verdict beside the title is the server&apos;s, measured against the
          tolerance held in Tournament Configuration.
          <div className="hidenote">
            <b>No completed / appointed split —</b> the design draft shows each count as
            <i> completed / appointed</i>. This screen is scoped to one match day and the API
            does not report on this route whether a match has been played, so a split would be
            invented rather than measured. The figures above are appointments.
          </div>
        </TipsFold>
      </div>
    </section>
  );
}
